import * as Api from '@ViewModels';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import { invalidateEventAutomationCoverage } from '../../../Surveys/useEventAutomationCoverage';
import { AUTOMATION_QUERY_KEYS, AUTOMATION_TEMPLATE_MUTATION_KEY } from '../../queryKeys';
import { invalidateAutomationTemplateDraftStateQuery } from '../../queries/useAutomationTemplateHasDraftQuery';
import { invalidateAllAutomationTemplatesQueries } from '../../queries/useAutomationTemplatesQuery';

export function useCommitAutomationDraftMutation({
	onSuccess,
	onError,
}: {
	onSuccess?: (data: Api.IAutomationTemplate) => void;
	onError?: (error: Api.IOperationResultNoValue) => void;
} = {}) {
	const userSession = useUserSession();
	const queryClient = useQueryClient();

	return useMutation({
		mutationKey: AUTOMATION_TEMPLATE_MUTATION_KEY,
		mutationFn: ({
			id,
			impersonationContext,
			ignorePromptValidation,
		}: {
			id: string;
			impersonationContext?: Api.IImpersonationContext;
			ignorePromptValidation?: boolean;
		}) => {
			return userSession.webServiceHelper.callAsync<Api.IAutomationTemplate>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `AutomationTemplate/${encodeURIComponent(id)}/Draft/commit`,
					queryParams: ignorePromptValidation ? { ignorePromptValidation: true } : undefined,
					impersonationContext,
				}),
				'POST'
			);
		},
		onSuccess: (data, variables) => {
			queryClient.invalidateQueries({ queryKey: AUTOMATION_QUERY_KEYS.template(variables.id) });
			invalidateAutomationTemplateDraftStateQuery(variables.id);
			invalidateAllAutomationTemplatesQueries();
			invalidateEventAutomationCoverage();
			onSuccess?.(data);
		},
		onError,
	});
}
